import { useState, useEffect } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import {
  Box,
  Container,
  Icon,
  Image,
  Text,
  TransparentButton,
  shouldForwardProp,
} from '@redriverpress/system'
import R from 'ramda'

import Tooltip from 'Common/components/ClickableTooltip'

import { LOC_LIST } from '../constants'
import {
  useStudentOnboardingContext,
  setLocale,
} from '../../context/onboardingStudentContext'

const MenuItem = styled(TransparentButton)
  .withConfig({ shouldForwardProp })
  .attrs(props => ({
    borderRadius: 'rounded',
    color: 'black',
    display: 'block',
    fontSize: 0,
    fontWeight: 600,
    py: 2,
    px: ['0.75rem', null, 3], // 0.75rem on mobile so items in full width menus line up w/ site container
    textAlign: 'left',
    width: 1,
    as: props.as || (props.href ? 'a' : 'button'),
  }))`
  transition: unset; /* Override link global style */

  /* Radix manages focus programatically, so it's triggered on hover as well */
  &:focus {
    background: ${props =>
      props.disabled ? 'transparent' : props.theme.colors.blue500};
    color: ${props =>
      props.disabled ? 'currentColor' : props.theme.colors.white};
    outline: none;
  }
`

const LangButton = styled(TransparentButton).attrs(() => ({
  mx: 1,
  mb: 2,
  border: '1px solid',
  borderColor: 'gray300',
  borderRadius: 'small',
}))`
  &:hover {
    background-color: ${props => props.theme.colors.blue100};
    border-color: ${props => props.theme.colors.blue500};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${props => props.theme.colors.white},
      0 0 0 2px ${props => props.theme.colors.blue700};
    z-index: 1;
  }

  background-color: ${props => props.selected && props.theme.colors.blue100};
  border-color: ${props => props.selected && props.theme.colors.blue500};
`

const MotionIcon = motion(Icon)

const TooltipContent = () => (
  <Box>
    <Text>Choose a language to help you sign up or log in to Ellii.</Text>

    <Text>
      <strong>Still having trouble?</strong> Ask your teacher to help you sign
      up or log in.
    </Text>
  </Box>
)

const Dropdown = () => {
  const [{ locale }, dispatch] = useStudentOnboardingContext()
  const [isOpen, setOpen] = useState(false)

  const currentTab = R.find(R.propEq('lang', locale), LOC_LIST)

  useEffect(() => {
    // Close menu when locale changes
    setOpen(false)
  }, [locale])

  return (
    <DropdownMenu.Root onOpenChange={o => setOpen(o)}>
      <DropdownMenu.Trigger asChild>
        <TransparentButton
          position='sticky'
          top='100vh'
          zIndex={1}
          bg='white'
          boxShadow={2}
          py={3}
          px={0}
          textAlign='left'
          width={1}
        >
          <Container
            display='flex'
            alignItems='center'
            fontWeight='600'
            color='black'
          >
            <Text fontSize={1}>
              Help in other languages{' '}
              {currentTab ? `(${currentTab?.copy})` : ''}
            </Text>
            <MotionIcon
              icon='chevron-down'
              ml='auto'
              animate={{ rotate: isOpen ? 180 : 0 }}
            />
          </Container>
        </TransparentButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content asChild align='center' sideOffset={4}>
        <Box
          bg='white'
          width='100vw'
          boxShadow='inset 0px 4px 4px rgba(0, 0, 0, 0.1)'
          p={1}
          position='relative'
          mt='-5px'
        >
          {LOC_LIST.map(loc => (
            <DropdownMenu.Item key={loc.lang} asChild>
              <MenuItem onClick={() => dispatch(setLocale(loc.lang))}>
                <Text fontSize={1}>{loc.copy}</Text>
              </MenuItem>
            </DropdownMenu.Item>
          ))}
        </Box>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

const ButtonList = ({ ...props }) => {
  const [{ locale }, dispatch] = useStudentOnboardingContext()

  return (
    <Box {...props}>
      <Box display='flex' alignItems='baseline'>
        <Text mx={1} fontSize={3} mb={2}>
          Help in other languages
        </Text>

        {/* ----- TOOLTIP ----- */}
        <Tooltip icon='question-circle' content={<TooltipContent />} />
      </Box>

      <Box display='flex' flexWrap='wrap'>
        {LOC_LIST.map(loc => (
          <LangButton
            key={loc.lang}
            selected={
              locale === loc.lang || (!locale && loc.lang === 'english')
            }
            width='auto'
            px={3}
            display='flex'
            alignItems='center'
            justifyContent='center'
            onClick={() => dispatch(setLocale(loc.lang))}
          >
            <Image src={loc.flag} alt={loc.copy} mr={2} />
            <Text fontSize={1}>{loc.copy}</Text>
          </LangButton>
        ))}
      </Box>
    </Box>
  )
}

export default function LanguageHelp({ ...props }) {
  const [{ isSm }] = useStudentOnboardingContext()
  return isSm ? <Dropdown /> : <ButtonList {...props} />
}
